import React from 'react';

/**
 * Shared background for all auth pages (Login, Signup, ForgotPassword, ResetPassword).
 * Aubergine gradient with scattered restaurant/POS SVG icons + depth orbs.
 *
 * Palette matches Login's brand panel (Login.tsx: "brand hero: aubergine ramp,
 * matches --band-bg family") so the whole funnel reads as one product. It was
 * previously a Slate/Indigo/Violet ramp — Login covers this layer with its own
 * panel so it looked right, but Signup / ForgotPassword / ResetPassword show it
 * directly and rendered indigo.
 */
const AuthBackground: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto"
      style={{
        background: 'linear-gradient(135deg, #3A2540 0%, #241826 45%, #140D16 100%)',
      }}
    >
      {/* Scattered restaurant/POS icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            {/* Fork & Knife */}
            <symbol id="icon-utensils" viewBox="0 0 24 24">
              <path d="M3 2v7c0 1.1.9 2 2 2h2v11h2V11h2c1.1 0 2-.9 2-2V2H3zm4 7H5V4h2v5zm10-7c-2.2 0-4 1.8-4 4v5c0 1.1.9 2 2 2h1v11h2V13h1c1.1 0 2-.9 2-2V6c0-2.2-1.8-4-4-4z" fill="white" fillOpacity="0.15"/>
            </symbol>
            {/* QR Code */}
            <symbol id="icon-qr" viewBox="0 0 24 24">
              <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm13-2h1v2h-2v-1h1v-1zm-4 0h1v1h-1v-1zm2 0h1v1h-1v-1zm4 0h1v3h-1v1h-2v-1h1v-2h1v-1zm-4 3h1v1h-1v-1zm2 1h1v1h-1v-1zm-2 1h1v2h-2v-1h1v-1zm2 2h3v1h-3v-1z" fill="white" fillOpacity="0.15"/>
            </symbol>
            {/* Receipt/Bill */}
            <symbol id="icon-receipt" viewBox="0 0 24 24">
              <path d="M19.5 3.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5zM19 19.09H5V4.91h14v14.18zM7 15h10v2H7v-2zm0-4h10v2H7v-2zm0-4h10v2H7V7z" fill="white" fillOpacity="0.15"/>
            </symbol>
            {/* Chef Hat */}
            <symbol id="icon-chef" viewBox="0 0 24 24">
              <path d="M12 2C9.5 2 7.5 4 7.5 6.5c0 .28.03.56.08.83C5.64 7.84 4 9.71 4 12c0 2.76 2.24 5 5 5v3h6v-3c2.76 0 5-2.24 5-5 0-2.29-1.64-4.16-3.58-4.67.05-.27.08-.55.08-.83C16.5 4 14.5 2 12 2zm3 16H9v-1h6v1zm2-4c0 1.1-.9 2-2 2H9c-1.1 0-2-.9-2-2s.9-2 2-2h6c1.1 0 2 .9 2 2z" fill="white" fillOpacity="0.15"/>
            </symbol>
            {/* Coffee Cup */}
            <symbol id="icon-coffee" viewBox="0 0 24 24">
              <path d="M2 21h18v-2H2v2zM20 8h-2V5H4v3H2v5c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2v-1h4c1.1 0 2-.9 2-2v-1c0-1.1-.9-2-2-2zm-6 5H4V8h10v5zm6-3h-4V8h4v2zM6 1h2v3H6V1zm4 0h2v3h-2V1zm4 0h2v3h-2V1z" fill="white" fillOpacity="0.15"/>
            </symbol>
            {/* Plate / Dish */}
            <symbol id="icon-plate" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" fill="white" fillOpacity="0.15"/>
            </symbol>
            {/* Bell / Service Bell */}
            <symbol id="icon-bell" viewBox="0 0 24 24">
              <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z" fill="white" fillOpacity="0.15"/>
            </symbol>
            {/* Wine Glass */}
            <symbol id="icon-wine" viewBox="0 0 24 24">
              <path d="M6 2l.01 6L10 12.5V20H6v2h12v-2h-4v-7.5L17.99 8 18 2H6zm9.16 8H8.84L7 7.83V4h10v3.83L15.16 10z" fill="white" fillOpacity="0.15"/>
            </symbol>
            {/* Shopping Bag / Order */}
            <symbol id="icon-bag" viewBox="0 0 24 24">
              <path d="M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6-2c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2zm6 16H6V8h2v2c0 .55.45 1 1 1s1-.45 1-1V8h4v2c0 .55.45 1 1 1s1-.45 1-1V8h2v12z" fill="white" fillOpacity="0.15"/>
            </symbol>
            {/* Star / Rating */}
            <symbol id="icon-star" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="white" fillOpacity="0.12"/>
            </symbol>
            {/* Pizza */}
            <symbol id="icon-pizza" viewBox="0 0 24 24">
              <path d="M12 2C8.43 2 5.23 3.54 3.01 6L12 22l8.99-16C18.78 3.54 15.57 2 12 2zM7 7c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm5 8c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" fill="white" fillOpacity="0.12"/>
            </symbol>
            {/* Cash Register / POS */}
            <symbol id="icon-pos" viewBox="0 0 24 24">
              <path d="M4 6h16v2H4V6zm0-4h16v2H4V2zm0 8h16v12H4V10zm2 2v8h12v-8H6zm2 2h3v2H8v-2zm5 0h3v2h-3v-2zm-5 3h3v2H8v-2zm5 0h3v2h-3v-2z" fill="white" fillOpacity="0.15"/>
            </symbol>
          </defs>

          {/* Row 1 — top area */}
          <use href="#icon-utensils" x="2%" y="3%" width="44" height="44" transform="rotate(-15, 30, 30)" />
          <use href="#icon-qr" x="20%" y="1%" width="40" height="40" transform="rotate(10, 100, 20)" />
          <use href="#icon-coffee" x="43%" y="2%" width="42" height="42" transform="rotate(-8, 200, 25)" />
          <use href="#icon-star" x="66%" y="4%" width="36" height="36" transform="rotate(20, 300, 35)" />
          <use href="#icon-bell" x="86%" y="1%" width="40" height="40" transform="rotate(-12, 400, 20)" />

          {/* Row 2 — upper-mid */}
          <use href="#icon-receipt" x="6%" y="17%" width="38" height="38" transform="rotate(12, 50, 100)" />
          <use href="#icon-pizza" x="28%" y="19%" width="46" height="46" transform="rotate(-20, 140, 110)" />
          <use href="#icon-plate" x="53%" y="15%" width="40" height="40" transform="rotate(8, 240, 90)" />
          <use href="#icon-bag" x="76%" y="18%" width="42" height="42" transform="rotate(-5, 350, 105)" />
          <use href="#icon-pos" x="93%" y="14%" width="38" height="38" transform="rotate(15, 420, 85)" />

          {/* Row 3 — mid */}
          <use href="#icon-wine" x="1%" y="36%" width="40" height="40" transform="rotate(-10, 20, 200)" />
          <use href="#icon-chef" x="16%" y="40%" width="44" height="44" transform="rotate(18, 90, 220)" />
          <use href="#icon-star" x="38%" y="35%" width="32" height="32" transform="rotate(-25, 180, 195)" />
          <use href="#icon-utensils" x="60%" y="38%" width="42" height="42" transform="rotate(12, 280, 215)" />
          <use href="#icon-receipt" x="80%" y="35%" width="40" height="40" transform="rotate(-8, 370, 200)" />

          {/* Row 4 — lower-mid */}
          <use href="#icon-qr" x="4%" y="56%" width="42" height="42" transform="rotate(8, 35, 310)" />
          <use href="#icon-bag" x="23%" y="60%" width="38" height="38" transform="rotate(-14, 120, 330)" />
          <use href="#icon-coffee" x="46%" y="54%" width="40" height="40" transform="rotate(22, 210, 300)" />
          <use href="#icon-bell" x="68%" y="58%" width="44" height="44" transform="rotate(-6, 310, 320)" />
          <use href="#icon-pizza" x="88%" y="55%" width="38" height="38" transform="rotate(10, 400, 305)" />

          {/* Row 5 — lower */}
          <use href="#icon-plate" x="3%" y="74%" width="40" height="40" transform="rotate(-18, 25, 400)" />
          <use href="#icon-pos" x="18%" y="78%" width="42" height="42" transform="rotate(5, 100, 420)" />
          <use href="#icon-wine" x="40%" y="76%" width="38" height="38" transform="rotate(-12, 190, 410)" />
          <use href="#icon-chef" x="63%" y="74%" width="44" height="44" transform="rotate(15, 290, 400)" />
          <use href="#icon-star" x="83%" y="77%" width="36" height="36" transform="rotate(-20, 380, 415)" />

          {/* Row 6 — bottom */}
          <use href="#icon-utensils" x="10%" y="90%" width="38" height="38" transform="rotate(10, 65, 485)" />
          <use href="#icon-receipt" x="33%" y="92%" width="42" height="42" transform="rotate(-8, 160, 495)" />
          <use href="#icon-qr" x="56%" y="89%" width="40" height="40" transform="rotate(14, 260, 480)" />
          <use href="#icon-coffee" x="78%" y="91%" width="38" height="38" transform="rotate(-16, 360, 490)" />
        </svg>
      </div>

      {/* Depth orbs — white lifts, as on Login's panel, plus one saffron warm
          note (#E8A13C = --rail-accent). Were indigo/violet. */}
      <div className="absolute rounded-full blur-3xl" style={{ top: '-10%', left: '-5%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)' }} />
      <div className="absolute rounded-full blur-3xl" style={{ bottom: '-15%', right: '-10%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)' }} />
      <div className="absolute rounded-full blur-3xl" style={{ top: '40%', right: '15%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(232,161,60,0.10) 0%, transparent 70%)' }} />

      {children}
    </div>
  );
};

export default AuthBackground;
